import { useAlternativeRegistrationModal } from '@exam-center/alternative-registration';
import { IdType, ExamineeRegistrationStatus } from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { useTimeGate } from '@exam-center/server-time';
import {
  Box,
  Button,
  CalendarTodayIcon,
  EditOutlinedIcon,
  Flex,
  Grid,
  PaperSection,
  ReadonlyField,
  Text,
  Tooltip,
} from '@shared/components';
import { WithPermission } from '@shared/permissions';
import { formatDateTh, dayjs } from '@shared/utils';
import { useMemo } from 'react';

import { DOCUMENT_TYPE } from '../../constants';
import { REGISTRATION_TYPE_TEXT } from '../registration-type-label/registration-type-label';

export interface RegistrationInformationDetailsProps {
  slotProps?: {
    examEventExamineeId: string;
    registrationStatus: ExamineeRegistrationStatus;
    examStartedAt: Date;
    name?: {
      title: string;
      firstName: string;
      middleName?: string | null;
      lastName: string;
    };
    document?: {
      registrationType?: string | null;
      documentType?: string | null;
      idNumber: string;
      idType: IdType;
    };
    dateOfBirth?: Date | null;
    expiredDate?: Date | null;
    reason?: string;
    countryCode?: string | null;
  };
}

export default function RegistrationInformationDetails(
  props: Readonly<RegistrationInformationDetailsProps>,
) {
  const { slotProps } = props;
  const {
    document,
    registrationStatus,
    examStartedAt,
    name,
    dateOfBirth,
    expiredDate,
    reason,
    countryCode,
  } = slotProps ?? {};

  const { setAction } = useAlternativeRegistrationModal();

  const examDayStart = useMemo(
    () => (examStartedAt ? dayjs(examStartedAt).startOf('day').toDate() : new Date()),
    [examStartedAt],
  );
  const thirtyMinAfterStart = useMemo(
    () => (examStartedAt ? dayjs(examStartedAt).add(30, 'minute').toDate() : new Date()),
    [examStartedAt],
  );

  const isOnOrAfterExamDate = useTimeGate(examDayStart);
  const isMoreThan30MinutesAfterExamStartTime = useTimeGate(thirtyMinAfterStart);

  const isBeforeExamDate = !isOnOrAfterExamDate;

  const handleRegisterWithOtherDocuments = () => {
    setAction({
      type:
        document?.idType === IdType.Passport
          ? 'register-foreigner-examinee-with-alternative-document'
          : 'register-examinee-with-alternative-document',
      examEventExamineeId: slotProps?.examEventExamineeId ?? '',
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Flex
        sx={(theme) => ({
          flexDirection: 'column',
          gap: theme.spacing(6),
          width: '100%',
        })}
      >
        <PaperSection
          title='การลงทะเบียนผู้เข้าสอบ'
          sx={{
            width: '100%',
          }}
          endAdornment={
            registrationStatus === ExamineeRegistrationStatus.NotRegistered ? (
              <WithPermission requiredPermission={APP_PERMISSIONS.PROCTORING.REGISTRATION.UPDATE}>
                <Tooltip
                  title={
                    isBeforeExamDate
                      ? 'ลงทะเบียนได้เฉพาะวันสอบเท่านั้น'
                      : isMoreThan30MinutesAfterExamStartTime
                        ? 'ไม่สามารถลงทะเบียนได้\nเนื่องจากสิ้นสุดช่วงเวลาการลงทะเบียนแล้ว'
                        : ''
                  }
                  arrow
                  placement='left'
                >
                  <Button
                    variant='outlined'
                    color='secondary'
                    sx={(theme) => ({
                      gap: theme.spacing(2),
                      '&.Mui-disabled': {
                        pointerEvents: 'auto',
                        bgcolor: theme.palette.grey[100],
                        ':hover': {
                          borderColor: theme.palette.grey['300'],
                        },
                      },
                    })}
                    onClick={handleRegisterWithOtherDocuments}
                    disabled={isBeforeExamDate || isMoreThan30MinutesAfterExamStartTime}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 20 }} />
                    ลงทะเบียนด้วยเอกสารอื่น
                  </Button>
                </Tooltip>
              </WithPermission>
            ) : null
          }
        >
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(6),
              width: '100%',
              padding: theme.spacing(6),
            })}
          >
            <Text
              variant='h4'
              sx={(theme) => ({
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                pl: theme.spacing(4),
              })}
            >
              Registration Information
            </Text>
            <Flex sx={(theme) => ({ gap: theme.spacing(6), m: 6 })}>
              <Text
                variant='body1Semibold'
                sx={{
                  maxWidth: '240px',
                  width: '100%',
                }}
              >
                ข้อมูลการลงทะเบียน
              </Text>
              <Grid
                sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6), width: '100%' })}
              >
                <Flex sx={(theme) => ({ gap: theme.spacing(4), width: '66%' })}>
                  <ReadonlyField
                    content={
                      (document?.registrationType &&
                        REGISTRATION_TYPE_TEXT[
                          document.registrationType as keyof typeof REGISTRATION_TYPE_TEXT
                        ]) ||
                      '-'
                    }
                    label='ประเภทการลงทะเบียน'
                  />
                  <ReadonlyField
                    content={
                      (document?.documentType &&
                        DOCUMENT_TYPE[document.documentType as keyof typeof DOCUMENT_TYPE]) ||
                      '-'
                    }
                    label='ประเภทเอกสาร'
                  />
                </Flex>
                <Flex sx={(theme) => ({ gap: theme.spacing(4), width: '66%' })}>
                  {document?.idType === IdType.CitizenCard ? null : (
                    <ReadonlyField content={countryCode ?? '-'} label='รหัสประเทศ' />
                  )}
                  <ReadonlyField
                    content={document?.idNumber ?? ''}
                    label={
                      document?.idType === IdType.CitizenCard ? 'เลขหน้าบัตรประชาชน' : 'เลขพาสปอร์ต'
                    }
                  />
                </Flex>
                <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
                  <Flex sx={(theme) => ({ gap: theme.spacing(4), width: '66%' })}>
                    <Box sx={{ maxWidth: '200px', width: '100%' }}>
                      <ReadonlyField content={name?.title ?? ''} label='คำนำหน้า' />
                    </Box>
                    <ReadonlyField content={name?.firstName ?? ''} label='ชื่อ' />
                    <ReadonlyField content={name?.middleName ?? ''} label='ชื่อกลาง' />
                  </Flex>
                  <Flex sx={{ width: '30%' }}>
                    <ReadonlyField content={name?.lastName ?? ''} label='นามสกุล' />
                  </Flex>
                </Flex>
                <Flex sx={(theme) => ({ gap: theme.spacing(4), width: '66%' })}>
                  <Box sx={{ maxWidth: '200px', width: '100%' }}>
                    <ReadonlyField
                      content={
                        <Flex sx={{ justifyContent: 'space-between' }}>
                          <Text>{dateOfBirth ? formatDateTh(dateOfBirth) : '-'}</Text>
                          <CalendarTodayIcon sx={{ pointerEvents: 'none', fontSize: 24 }} />
                        </Flex>
                      }
                      label='วันเดือนปีเกิด'
                    />
                  </Box>
                  <Box sx={{ maxWidth: '200px', width: '100%' }}>
                    <ReadonlyField
                      content={
                        <Flex sx={{ justifyContent: 'space-between' }}>
                          <Text>{expiredDate ? formatDateTh(expiredDate) : '-'}</Text>
                          <CalendarTodayIcon sx={{ pointerEvents: 'none', fontSize: 24 }} />
                        </Flex>
                      }
                      label='วันบัตรหมดอายุ'
                    />
                  </Box>
                </Flex>
                <Flex sx={(theme) => ({ gap: theme.spacing(4), width: '66%' })}>
                  <ReadonlyField content={reason ?? '-'} label='หมายเหตุการลงทะเบียน' />
                </Flex>
              </Grid>
            </Flex>
          </Flex>
        </PaperSection>
      </Flex>
    </Box>
  );
}
